import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';

export type IntegrationCategory =
  | 'messaging'
  | 'commerce'
  | 'payments'
  | 'pos'
  | 'social'
  | 'ads'
  | 'scheduling'
  | 'automation';

export const INTEGRATION_CATEGORY_BADGE: Record<
  IntegrationCategory,
  { label: string; bg: string; fg: string }
> = {
  messaging: { label: 'Messaging', bg: '#E0F5F1', fg: '#0E7C66' },
  commerce: { label: 'Commerce', bg: '#E7F7EC', fg: '#076A2D' },
  payments: { label: 'Payments', bg: '#E8F1FD', fg: '#2563EB' },
  pos: { label: 'Point of sale', bg: '#FFFAEB', fg: '#B54708' },
  social: { label: 'Social accounts', bg: '#FDE8F4', fg: '#B83280' },
  ads: { label: 'Ads', bg: '#EEF4FF', fg: '#3538CD' },
  scheduling: { label: 'Scheduling', bg: '#F4F3FF', fg: '#5925DC' },
  automation: { label: 'Automation', bg: '#FFF4ED', fg: '#C4320A' },
};

const MAX_META_PILLS = 2;

const PILL_CLASS = 'rounded-full px-2 py-0.5 text-[11px] font-semibold';

export const IntegrationCardSkeleton = () => (
  <div className="h-[218px] animate-pulse rounded-[14px] border border-[#EDF1F7] bg-[#F9FAFB]" />
);

export function IntegrationCard({
  name,
  description,
  category,
  gradient,
  icon,
  iconFill = false,
  connected,
  pending = false,
  statusLabel,
  metaPills,
  actionLabel,
  disabled = false,
  onOpen,
}: {
  name: string;
  description: string;
  category: IntegrationCategory;
  gradient: string;
  icon: string | React.ElementType;
  iconFill?: boolean;
  connected: boolean;
  pending?: boolean;
  statusLabel?: string;
  metaPills?: string[];
  actionLabel?: string;
  disabled?: boolean;
  onOpen: () => void;
}) {
  const Icon = icon;
  const badge = INTEGRATION_CATEGORY_BADGE[category];

  const pills = (metaPills ?? []).filter((label) => label.trim().length > 0);
  const shownPills = pills.slice(0, MAX_META_PILLS);
  const overflowPills = pills.slice(MAX_META_PILLS);
  const showCategoryPill = shownPills.length === 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-[14px] border border-[#EDF1F7] bg-white text-left shadow-[0_1px_3px_rgba(16,24,40,0.04)] transition-shadow hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-60"
    >
      <div className="flex flex-col p-6 pb-4">
        <div className="mb-3 flex items-center gap-3">
          {iconFill && typeof Icon === 'string' ? (
            <img
              src={Icon}
              alt=""
              aria-hidden
              className="h-[55px] w-[55px] shrink-0 rounded-[12px] transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div
              className={`flex shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br ${gradient} p-3 transition-transform duration-200 group-hover:scale-105`}
            >
              {typeof Icon === 'string' ? (
                <img src={Icon} alt="" aria-hidden className="h-7 w-7" />
              ) : (
                <Icon className="h-7 w-7 text-white" />
              )}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <h3 className="mb-1 break-words text-[18px] font-bold leading-tight tracking-tight text-[#101828]">
              {name}
            </h3>
            <p
              title={description}
              className="truncate text-[13px] font-medium leading-[1.5] text-[#6A7282]"
            >
              {description}
            </p>
          </div>
        </div>

        <div className="flex min-h-[22px] items-center gap-2 overflow-hidden">
          {showCategoryPill ? (
            <span
              className={`${PILL_CLASS} inline-flex shrink-0 items-center`}
              style={{ background: badge.bg, color: badge.fg }}
            >
              {badge.label}
            </span>
          ) : null}

          {shownPills.map((label, i) => (
            <span
              key={`${label}-${i}`}
              title={label}
              className={`${PILL_CLASS} max-w-[130px] truncate`}
              style={{ background: badge.bg, color: badge.fg }}
            >
              {label}
            </span>
          ))}

          {overflowPills.length > 0 ? (
            <span
              title={overflowPills.join(', ')}
              className={`${PILL_CLASS} shrink-0`}
              style={{ background: badge.bg, color: badge.fg }}
            >
              +{overflowPills.length}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-auto flex flex-col justify-end p-6 pt-0">
        <div className="flex h-10 items-center justify-between">
          {pending ? (
            <span
              aria-busy="true"
              className="inline-flex animate-pulse items-center text-[13px] font-medium text-[#98A2B3]"
            >
              Checking…
            </span>
          ) : connected ? (
            <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-cta">
              <CheckCircle className="h-3.5 w-3.5 shrink-0 text-brand" />
              {statusLabel ?? 'Connected'}
            </span>
          ) : (
            <span className="text-[13px] font-medium text-[#6A7282]">
              {statusLabel ?? 'Not connected'}
            </span>
          )}

          <span className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-brand">
            {pending ? 'Open' : actionLabel ?? (connected ? 'Open' : 'Connect')}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </button>
  );
}

export default IntegrationCard;
